import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import API from "../../utils/api";
import styles from "./DashboardCharts.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function DashboardCharts() {
  const [chartData, setChartData] = useState({
    products: { labels: [], data: [] },
    categories: { labels: [], data: [] },
    brands: { labels: [], data: [] },
    monthlyProducts: { labels: [], data: [] },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      // Fetch products, categories, and brands data
      const [productsRes, categoriesRes, brandsRes] = await Promise.all([
        fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "fetch" }),
        }),
        fetch(`${API.base}/categories.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "fetch" }),
        }),
        fetch(`${API.base}/brands.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "fetch" }),
        }),
      ]);

      const products = await productsRes.json();
      const categories = await categoriesRes.json();
      const brands = await brandsRes.json();

      const productsList = Array.isArray(products) ? products : products.data || [];
      const categoriesList = Array.isArray(categories) ? categories : categories.data || [];
      const brandsList = Array.isArray(brands) ? brands : brands.data || [];

      // Process category distribution
      const categoryCounts = {};
      productsList.forEach((product) => {
        const catId = product.category_id;
        if (catId) {
          const category = categoriesList.find((c) => c.id === catId);
          const catName = category?.categoryName || "Uncategorized";
          categoryCounts[catName] = (categoryCounts[catName] || 0) + 1;
        }
      });

      // Process brand distribution
      const brandCounts = {};
      productsList.forEach((product) => {
        const brandId = product.brand_id;
        if (brandId) {
          const brand = brandsList.find((b) => b.id === brandId);
          const brandName = brand?.brandName || "Unknown";
          brandCounts[brandName] = (brandCounts[brandName] || 0) + 1;
        }
      });

      // Process monthly products
      const monthlyCounts = {};
      productsList.forEach((product) => {
        if (product.created_at) {
          const date = new Date(product.created_at);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
          monthlyCounts[monthKey] = (monthlyCounts[monthKey] || 0) + 1;
        }
      });

      // Sort and limit top categories
      const topCategories = Object.entries(categoryCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);

      // Sort and limit top brands
      const topBrands = Object.entries(brandCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);

      // Sort monthly data
      const sortedMonths = Object.keys(monthlyCounts).sort();
      const last6Months = sortedMonths.slice(-6);

      setChartData({
        products: {
          labels: topCategories.map(([name]) => name),
          data: topCategories.map(([, count]) => count),
        },
        categories: {
          labels: topBrands.map(([name]) => name),
          data: topBrands.map(([, count]) => count),
        },
        brands: {
          labels: ["Published", "Draft"],
          data: [
            productsList.filter((p) => p.status === "publish").length,
            productsList.filter((p) => p.status === "draft").length,
          ],
        },
        monthlyProducts: {
          labels: last6Months.map((month) => {
            const [year, monthNum] = month.split("-");
            const date = new Date(year, parseInt(monthNum) - 1);
            return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
          }),
          data: last6Months.map((month) => monthlyCounts[month] || 0),
        },
      });
    } catch (err) {
      console.error("Failed to fetch chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: "bold",
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: "bold",
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: "bold",
        },
      },
    },
  };

  if (loading) {
    return <div className={styles.loading}>Loading charts...</div>;
  }

  return (
    <div className={styles.chartsContainer}>
      <div className={styles.chartsGrid}>
        {/* Products by Category */}
        <div className={styles.chartCard}>
          <Bar
            data={{
              labels: chartData.products.labels,
              datasets: [
                {
                  label: "Products",
                  data: chartData.products.data,
                  backgroundColor: [
                    "rgba(54, 162, 235, 0.8)",
                    "rgba(255, 99, 132, 0.8)",
                    "rgba(255, 206, 86, 0.8)",
                    "rgba(75, 192, 192, 0.8)",
                    "rgba(153, 102, 255, 0.8)",
                  ],
                  borderColor: [
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 99, 132, 1)",
                    "rgba(255, 206, 86, 1)",
                    "rgba(75, 192, 192, 1)",
                    "rgba(153, 102, 255, 1)",
                  ],
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              ...barChartOptions,
              plugins: {
                ...barChartOptions.plugins,
                title: {
                  ...barChartOptions.plugins.title,
                  text: "Top 5 Categories by Product Count",
                },
              },
            }}
          />
        </div>

        {/* Products by Brand */}
        <div className={styles.chartCard}>
          <Bar
            data={{
              labels: chartData.categories.labels,
              datasets: [
                {
                  label: "Products",
                  data: chartData.categories.data,
                  backgroundColor: [
                    "rgba(255, 99, 132, 0.8)",
                    "rgba(54, 162, 235, 0.8)",
                    "rgba(255, 206, 86, 0.8)",
                    "rgba(75, 192, 192, 0.8)",
                    "rgba(153, 102, 255, 0.8)",
                  ],
                  borderColor: [
                    "rgba(255, 99, 132, 1)",
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 206, 86, 1)",
                    "rgba(75, 192, 192, 1)",
                    "rgba(153, 102, 255, 1)",
                  ],
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              ...barChartOptions,
              plugins: {
                ...barChartOptions.plugins,
                title: {
                  ...barChartOptions.plugins.title,
                  text: "Top 5 Brands by Product Count",
                },
              },
            }}
          />
        </div>

        {/* Product Status Distribution */}
        <div className={styles.chartCard}>
          <Doughnut
            data={{
              labels: chartData.brands.labels,
              datasets: [
                {
                  label: "Products",
                  data: chartData.brands.data,
                  backgroundColor: [
                    "rgba(75, 192, 192, 0.8)",
                    "rgba(255, 99, 132, 0.8)",
                  ],
                  borderColor: [
                    "rgba(75, 192, 192, 1)",
                    "rgba(255, 99, 132, 1)",
                  ],
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              ...doughnutChartOptions,
              plugins: {
                ...doughnutChartOptions.plugins,
                title: {
                  ...doughnutChartOptions.plugins.title,
                  text: "Product Status Distribution",
                },
              },
            }}
          />
        </div>

        {/* Monthly Products Trend */}
        <div className={styles.chartCard}>
          <Line
            data={{
              labels: chartData.monthlyProducts.labels,
              datasets: [
                {
                  label: "Products Added",
                  data: chartData.monthlyProducts.data,
                  borderColor: "rgba(54, 162, 235, 1)",
                  backgroundColor: "rgba(54, 162, 235, 0.2)",
                  tension: 0.4,
                  fill: true,
                  pointRadius: 5,
                  pointHoverRadius: 7,
                },
              ],
            }}
            options={{
              ...lineChartOptions,
              plugins: {
                ...lineChartOptions.plugins,
                title: {
                  ...lineChartOptions.plugins.title,
                  text: "Products Added (Last 6 Months)",
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

